import { AttackSurfaceSelector } from '@/components/AttackSurfaceSelector';
import { BaseTag } from '@/components/chariot-ui';
import {
  frequencyLabel,
  SUBSCRIPTION_TIERS,
} from '@/sections/settings/subscription/constant';
import { formatPrice } from '@/sections/settings/subscription/util';
import { MonitoringCounts, SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { calculateAssetTotal } from '@/utils/monitoring';

function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-chariot-text-primary text-sm">{label}</span>
      <span
        className={cn(
          'text-chariot-text-primary text-sm font-semibold',
          valueClassName
        )}
      >
        {value}
      </span>
    </div>
  );
}

function CoverageValue({ current, limit }: { current: number; limit: number }) {
  const percentage = limit > 0 ? (current / limit) * 100 : 0;
  const isOver = limit > 0 && current > limit;
  const isApproaching = limit > 0 && !isOver && percentage >= 90;

  const textColor = isOver
    ? 'text-red-500'
    : isApproaching
      ? 'text-amber-600'
      : 'text-chariot-text-primary';

  return (
    <div className="flex items-center gap-1">
      <span className={cn('text-sm font-semibold', textColor)}>
        {current.toLocaleString()}
      </span>
      {limit > 0 && (
        <>
          <span className="text-chariot-text-secondary text-sm">/</span>
          <span className="text-chariot-text-primary text-sm font-semibold">
            {limit.toLocaleString()}
          </span>
        </>
      )}
    </div>
  );
}

export function SubscriptionSummary(
  props: SubscriptionConfiguration & {
    pricingCurrency: string;
    billingCycle: 'annual' | 'monthly';
    billingPaymentMethod: string;
    monitoringCounts?: MonitoringCounts;
  }
) {
  // Calculate term in months from start and end dates
  const calculateTermInMonths = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return 12; // Default to 12 months

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate year difference in months
    const yearsDiff = end.getFullYear() - start.getFullYear();
    const monthsDiff = end.getMonth() - start.getMonth();

    // Total months between dates
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // Add 1 if end day is greater than or equal to start day (full month)
    if (end.getDate() >= start.getDate()) {
      totalMonths += 1;
    }

    return Math.max(1, totalMonths); // Ensure at least 1 month
  };

  const termInMonths = calculateTermInMonths(props.startDate, props.endDate);

  const tierData = SUBSCRIPTION_TIERS.find(t => t.id === props.selectedTier);
  const tierDisplayName = tierData?.displayName || props.selectedTier;
  const enabledModules = tierData?.features
    ? Object.entries(tierData.features).flatMap(([, modules]) =>
        Object.entries(modules)
          .filter(([, enabled]) => enabled)
          .map(([name]) => name)
      )
    : [];

  const calculateCurrentApplications = () => {
    if (!props.monitoringCounts?.applications) return 0;
    return props.monitoringCounts.applications.webseed || 0;
  };

  const currentAssets = !props.monitoringCounts
    ? 0
    : calculateAssetTotal(props.monitoringCounts, props.attackSurface);
  const currentApplications = calculateCurrentApplications();

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
      {/* Row 1: Price (hero) */}
      <div className="p-6">
        <div className="flex items-baseline gap-2">
          <span className="text-chariot-brand-primary text-4xl font-bold">
            {formatPrice(props.estimatedCost, props.pricingCurrency)}
          </span>
          <span className="text-chariot-text-secondary text-lg">/ year</span>
        </div>
      </div>

      {/* Row 2: Contract details */}
      <div className="border-chariot-stroke space-y-3 border-t p-6">
        <DetailRow label="Start Date" value={props.startDate || 'Not set'} />
        <DetailRow label="End Date" value={props.endDate || 'Not set'} />
        <DetailRow
          label="Term"
          value={`${termInMonths} ${termInMonths === 1 ? 'month' : 'months'}`}
        />
        <DetailRow label="Payment Method" value={props.billingPaymentMethod} />
      </div>

      {/* Row 3: Tier + Product Modules */}
      <div className="border-chariot-stroke border-t p-6">
        <div className="mb-3 flex items-center gap-3">
          <span className="text-chariot-text-primary text-base font-semibold">
            Subscription Tier
          </span>
          <BaseTag
            className="border-chariot-brand-primary bg-chariot-background-secondary/40 capitalize"
            size="sm"
          >
            {tierDisplayName}
          </BaseTag>
        </div>
        {enabledModules.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {enabledModules.map(module => (
              <span
                key={module}
                className="bg-chariot-background-secondary text-chariot-text-secondary rounded-full px-3 py-1 text-xs"
              >
                {module}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Row 4: Coverage metrics */}
      <div className="border-chariot-stroke space-y-3 border-t p-6">
        <div className="flex items-center justify-between">
          <span className="text-chariot-text-primary text-sm">Assets</span>
          <CoverageValue current={currentAssets} limit={props.numberOfAssets} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-chariot-text-primary text-sm">
            Applications
          </span>
          <CoverageValue
            current={currentApplications}
            limit={props.numberOfApplications}
          />
        </div>
        {props.attackSurface.llm && (
          <div className="flex items-center justify-between">
            <span className="text-chariot-text-primary text-sm">
              LLM Endpoints
            </span>
            <CoverageValue
              current={props.monitoringCounts?.llm?.endpoints ?? 0}
              limit={props.numberOfLLMs ?? 0}
            />
          </div>
        )}
      </div>

      {/* Row 5: Scanning config */}
      <div className="border-chariot-stroke space-y-3 border-t p-6">
        <DetailRow
          label="Scanning Frequency"
          value={frequencyLabel(props.scanSchedule)}
        />
        <div className="flex items-center justify-between">
          <span className="text-chariot-text-primary text-sm">
            Attack Surface
          </span>
          <AttackSurfaceSelector
            value={{
              external: props.attackSurface.external || false,
              applications: props.attackSurface.applications || false,
              internal: props.attackSurface.internal || false,
              cloud: props.attackSurface.cloud || false,
              code: props.attackSurface.code || false,
              users: props.attackSurface.users || false,
              device: props.attackSurface.device || false,
              llm: props.attackSurface.llm || false,
            }}
            onChange={() => {}}
            variant="compact"
            showLabels={true}
          />
        </div>
      </div>
    </div>
  );
}
